// Reklam/kaynak bilgisini (UTM + click id) ziyaretin ilk sayfasinda yakalayip
// saklar; kullanici site ici gezinip formu baska bir sayfada doldursa bile
// lead'e dogru kaynak eklenir. Yalnizca tarayicida calisir.

export const TRACKING_PARAM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "fbclid",
    "msclkid",
    "ttclid",
] as const;

export type TrackingParamKey = (typeof TRACKING_PARAM_KEYS)[number];

export type TrackingParams = Record<TrackingParamKey, string>;

interface StoredAttribution {
    params: TrackingParams;
    referrer: string;
    landingPage: string;
    capturedAt: number;
}

const STORAGE_KEY = "cinik_attribution";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function emptyParams(): TrackingParams {
    return Object.fromEntries(TRACKING_PARAM_KEYS.map((key) => [key, ""])) as TrackingParams;
}

function getHashSearchParams(hash: string) {
    const normalizedHash = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!normalizedHash) {
        return new URLSearchParams();
    }

    const queryStartIndex = normalizedHash.indexOf("?");
    return new URLSearchParams(
        queryStartIndex >= 0 ? normalizedHash.slice(queryStartIndex + 1) : normalizedHash
    );
}

export function readParamsFromLocation(location: Location): TrackingParams {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = getHashSearchParams(location.hash);
    const params = emptyParams();

    for (const key of TRACKING_PARAM_KEYS) {
        params[key] = (searchParams.get(key) || hashParams.get(key) || "").slice(0, 500);
    }

    return params;
}

function hasAnyParam(params: TrackingParams) {
    return TRACKING_PARAM_KEYS.some((key) => Boolean(params[key]));
}

function isExternalReferrer(referrer: string) {
    if (!referrer) {
        return false;
    }

    try {
        return new URL(referrer).host !== window.location.host;
    } catch {
        return false;
    }
}

export function readStoredAttribution(): StoredAttribution | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const stored = JSON.parse(raw) as StoredAttribution;
        if (!stored?.capturedAt || Date.now() - stored.capturedAt > MAX_AGE_MS) {
            window.localStorage.removeItem(STORAGE_KEY);
            return null;
        }

        return { ...stored, params: { ...emptyParams(), ...stored.params } };
    } catch {
        return null;
    }
}

// Her sayfa gorunumunde cagrilir. URL'de kaynak parametresi varsa (yeni bir
// reklam tiklamasi) kaydi gunceller; yoksa ve henuz kayit yoksa dis
// referrer'i saklar. Parametresiz ic gezinme mevcut kaydi silmez.
export function captureAttribution() {
    if (typeof window === "undefined") {
        return;
    }

    const params = readParamsFromLocation(window.location);
    const referrer = isExternalReferrer(document.referrer) ? document.referrer : "";
    const existing = readStoredAttribution();

    if (!hasAnyParam(params) && (existing || !referrer)) {
        return;
    }

    const next: StoredAttribution = {
        params,
        referrer: referrer || existing?.referrer || "",
        landingPage: window.location.href,
        capturedAt: Date.now(),
    };

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Depolama kapaliysa (gizli mod vb.) yalnizca mevcut URL kullanilir.
    }
}

function readCookie(name: string) {
    const prefix = `${name}=`;
    const match = document.cookie.split("; ").find((part) => part.startsWith(prefix));
    if (!match) {
        return "";
    }

    try {
        return decodeURIComponent(match.slice(prefix.length));
    } catch {
        return match.slice(prefix.length);
    }
}

// Form gonderiminde kullanilacak nihai kaynak alanlari: once mevcut URL,
// sonra saklanan ilk-temas kaydi, gclid icin son olarak Zoho zcga.js cerezi.
export function resolveTrackingFields() {
    const current = readParamsFromLocation(window.location);
    const stored = readStoredAttribution();
    const params = emptyParams();

    for (const key of TRACKING_PARAM_KEYS) {
        params[key] = current[key] || stored?.params[key] || "";
    }

    // zcga.js gclid yoksa cerezi "undefined" metniyle yazar; bos sayilir.
    const rawZohoGclid = readCookie("gclid");
    const zohoGclid = rawZohoGclid === "undefined" || rawZohoGclid === "null" ? "" : rawZohoGclid;
    if (!params.gclid && zohoGclid) {
        params.gclid = zohoGclid;
    }

    return {
        ...params,
        page_url: window.location.href,
        referrer_url: stored?.referrer || document.referrer || "",
        zc_gad: zohoGclid || params.gclid,
    };
}
